import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FaRandom, FaMagic, FaEdit } from "react-icons/fa";
import * as api from "../../api/mathBank";
import MarkdownMathText from "./MarkdownMathText";
import QuestionDifficultyBadge from "./QuestionDifficultyBadge";
import QuestionAssetPreviewStrip from "../document-processing/QuestionAssetPreviewStrip";
import QuestionVariantEditor from "./QuestionVariantEditor";
import QuestionFamilyDialog from "./QuestionFamilyDialog";
import QuestionFamilyAnalysis from "./QuestionFamilyAnalysis";
import { questionDifficulties } from "../document-processing/mathBankQuestionOptions";

export default function QuestionFamilyReplacement(props) {
  const scope = `${props.subject}:${props.authToken || ""}:${props.question.id}`;
  return <ReplacementSession key={scope} {...props} />;
}

function ReplacementSession({ subject, authToken, question, families = [], excludeIds = [], onReplace, onQuestionSaved, onClose }) {
  const [resolvedQuestion, setResolvedQuestion] = useState(null);
  const [analysisStarted, setAnalysisStarted] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState("");
  const [stillUnassigned, setStillUnassigned] = useState(false);
  const active = useRef(true);
  const refreshRequest = useRef(0);
  const refreshInFlight = useRef(false);
  const callbacks = useRef({ onQuestionSaved, onClose });
  callbacks.current = { onQuestionSaved, onClose };
  const currentQuestion = resolvedQuestion?.source === question ? resolvedQuestion.value : question;

  useEffect(() => {
    active.current = true;
    return () => { active.current = false; refreshRequest.current += 1; };
  }, []);

  const close = () => {
    active.current = false;
    refreshRequest.current += 1;
    callbacks.current.onClose();
  };
  const refreshQuestion = async () => {
    if (!active.current || refreshInFlight.current) return;
    const request = ++refreshRequest.current;
    const isCurrent = () => active.current && refreshRequest.current === request;
    refreshInFlight.current = true;
    setRefreshing(true); setRefreshError(""); setStillUnassigned(false);
    try {
      const updated = await api.getQuestion({ subject, authToken, id: question.id });
      if (!isCurrent()) return;
      if (!updated || updated.id !== question.id) throw new Error("重新讀取的題目與原題不符");
      callbacks.current.onQuestionSaved?.(updated);
      if (!isCurrent()) return;
      setResolvedQuestion({ source: question, value: updated });
      setStillUnassigned(!updated.family?.id);
    } catch (reason) {
      if (isCurrent()) setRefreshError(`${reason.message || "題目讀取失敗"}。歸族結果已保留，可重新讀取題目。`);
    } finally {
      if (isCurrent()) { refreshInFlight.current = false; setRefreshing(false); }
    }
  };

  if (currentQuestion.family?.id) return <AssignedReplacement key={currentQuestion.family.id} subject={subject} authToken={authToken} question={currentQuestion} excludeIds={excludeIds} onReplace={onReplace} onQuestionSaved={onQuestionSaved} onClose={close} />;

  return <QuestionFamilyDialog title="換題" subtitle="保留原題位置與題數" onClose={close}>
    <Original><small>目前這題</small><MarkdownMathText source={currentQuestion.prompt_md} /><QuestionAssetPreviewStrip assets={(currentQuestion.assets || []).filter((asset) => asset.role === "prompt")} limit={4} /><Meta><span>{currentQuestion.grade?.name}</span><QuestionDifficultyBadge difficulty={currentQuestion.difficulty} /></Meta></Original>
    <GeneratePanel><h3>先建立題族，才能找相同解題方式的候選題。</h3><p>這題尚未歸族。AI 會提出題族框架與歸族建議；請審閱並確認套用，再從題庫或生成草稿換題。</p>
      {!analysisStarted && <Button type="button" $primary disabled={!authToken} onClick={() => setAnalysisStarted(true)}><FaMagic />AI 建立題族</Button>}
      {!authToken && <Message>請先登入才能建立題族。</Message>}
    </GeneratePanel>
    {analysisStarted && <AnalysisPanel><QuestionFamilyAnalysis subject={subject} authToken={authToken} families={families} initialQuestionIds={[question.id]} autoStart onChanged={refreshQuestion} /></AnalysisPanel>}
    {refreshing && <Message role="status">正在更新這題的題族與換題候選…</Message>}
    {stillUnassigned && <Message role="status">這題仍未歸入題族；請確認分類建議有包含並勾選此題，可重新分析後再確認歸族。</Message>}
    {refreshError && <Message $error role="alert">{refreshError}</Message>}
    {(refreshError || stillUnassigned) && <Actions><Button type="button" disabled={refreshing} onClick={refreshQuestion}>重新讀取題目</Button></Actions>}
  </QuestionFamilyDialog>;
}

function AssignedReplacement({ subject, authToken, question, excludeIds = [], onReplace, onQuestionSaved, onClose }) {
  const [pool, setPool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [batch, setBatch] = useState(null);
  const [draft, setDraft] = useState(null);
  const [difficulty, setDifficulty] = useState(question.difficulty || "U");
  const [retry, setRetry] = useState(0);
  const [draftRetry, setDraftRetry] = useState(0);
  const scope = `${subject}:${question.id}:${authToken}`;
  const currentScope = useRef(scope);
  const request = useRef(null);
  const inFlight = useRef(false);
  const exclusions = [...new Set([question.id, ...excludeIds])].sort().join(",");
  currentScope.current = scope;
  useEffect(() => () => { currentScope.current = null; }, []);
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(""); setPool(null);
    api.getFamilyReplacements({ subject, authToken, id: question.family.id, params: { question_id: question.id, exclude_ids: exclusions } })
      .then((result) => { if (!cancelled) setPool(result); })
      .catch((reason) => { if (!cancelled) setError(reason.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [subject, authToken, question.id, question.family.id, exclusions, retry]);

  useEffect(() => {
    if (!batch || !["queued", "running"].includes(batch.status)) return undefined;
    const activeScope = scope;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await api.getFamilyGenerationBatch({ subject, authToken, id: batch.id });
        if (!cancelled && currentScope.current === activeScope) { setBatch(result); setError(""); }
      } catch (reason) { if (!cancelled && currentScope.current === activeScope) setError(`${reason.message}。草稿工作已保留，可更新進度。`); }
    }, 2000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [batch, subject, authToken, scope]);

  useEffect(() => {
    if (!batch || ["queued", "running"].includes(batch.status)) return undefined;
    const accepted = batch.candidates?.find((candidate) => candidate.status === "accepted" && candidate.question_uuid);
    if (!accepted) { setError(batch.error || "這次沒有可用草稿，請重新生成。"); return undefined; }
    let cancelled = false;
    api.getQuestion({ subject, authToken, id: accepted.question_uuid })
      .then((result) => { if (!cancelled) { setDraft(result); setError(""); } })
      .catch((reason) => { if (!cancelled) setError(reason.message); });
    return () => { cancelled = true; };
  }, [batch, subject, authToken, draftRetry]);

  const generate = async () => {
    if (inFlight.current || !pool?.generation_supported) return;
    inFlight.current = true; setBusy(true); setError("");
    const activeScope = scope;
    const signature = `${scope}:${difficulty}`;
    if (request.current?.signature !== signature) request.current = { signature, id: crypto.randomUUID() };
    try {
      const result = await api.startFamilyGeneration({ subject, authToken, id: question.family.id, data: { seed_question_id: pool.generation_seed_question_id, grade_id: question.grade?.id || question.grade_id, count: 1, requested_difficulty: difficulty, request_id: request.current.id } });
      if (currentScope.current !== activeScope) return;
      request.current = null; setBatch(result);
    } catch (reason) { if (currentScope.current === activeScope) setError(reason.message); }
    finally { inFlight.current = false; if (currentScope.current === activeScope) setBusy(false); }
  };
  const handleUseQuestion = async (replacement) => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError("");
    const activeScope = scope;
    try {
      const used = await onReplace(replacement);
      if (currentScope.current !== activeScope) return;
      if (used !== false) onClose();
      else setError("原題或題目順序已變更，請關閉後重新選擇。");
    } catch (reason) { if (currentScope.current === activeScope) setError(reason.message); }
    finally { inFlight.current = false; if (currentScope.current === scope) setBusy(false); }
  };
  const pending = batch && ["queued", "running"].includes(batch.status);
  return <QuestionFamilyDialog title="同族換題" subtitle={`${question.family.name} · 保留原題位置與題數`} onClose={onClose}>
    {draft ? <QuestionVariantEditor subject={subject} authToken={authToken} question={draft} onSaved={(saved) => { setDraft(saved); onQuestionSaved?.(saved); }} onUse={handleUseQuestion} onClose={() => { setDraft(null); setBatch(null); setRetry((value) => value + 1); }} /> : <>
      <Original><small>目前這題</small><MarkdownMathText source={question.prompt_md} /><QuestionAssetPreviewStrip assets={(question.assets || []).filter((asset) => asset.role === "prompt")} limit={4} /><Meta><span>{question.grade?.name}</span><QuestionDifficultyBadge difficulty={question.difficulty} /></Meta></Original>
      <SectionHeader><div><h3>從題庫換題</h3><p>同題族、年級、題型與難度，排除已選題目。</p></div>{pool && <Count>{pool.available_count} 題可用</Count>}</SectionHeader>
      {loading ? <Message role="status">正在找合適的替換題…</Message> : pool?.questions?.length ? <Candidates>{pool.questions.map((candidate) => <Candidate key={candidate.id}>
        <MarkdownMathText source={candidate.prompt_md} /><QuestionAssetPreviewStrip assets={candidate.assets || []} role="prompt" limit={4} />
        <Meta><QuestionDifficultyBadge difficulty={candidate.difficulty} /><span>{candidate.question_source || "題庫題目"}</span></Meta>
        <details><summary>查看答案與詳解</summary><h4>答案</h4><MarkdownMathText source={candidate.answer_md} /><QuestionAssetPreviewStrip assets={candidate.assets || []} role="answer" limit={4} /><h4>詳解</h4><MarkdownMathText source={candidate.solution_md} /><QuestionAssetPreviewStrip assets={candidate.assets || []} role="solution" limit={4} /></details>
        <Actions><Button type="button" disabled={busy || pending} onClick={() => setDraft(candidate)}><FaEdit />編輯與審閱</Button><Button type="button" $primary disabled={busy || pending} onClick={() => handleUseQuestion(candidate)}><FaRandom />使用這題替換</Button></Actions>
      </Candidate>)}</Candidates> : pool && <Empty>目前沒有符合條件的庫存，可產生一題同族草稿再審閱。</Empty>}
      <GeneratePanel><div><h3><FaMagic /> AI 生成同族題</h3><p>新題先存私人草稿；可改數字、調整難度與詳解，確認後再使用。</p></div>
        <Actions><label>目標難度<select aria-label="換題目標難度" value={difficulty} disabled={busy || pending} onChange={(event) => setDifficulty(event.target.value)}>{questionDifficulties.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label><Button type="button" $primary disabled={loading || busy || pending || !pool?.generation_supported} onClick={generate}><FaMagic />{busy ? "送出中…" : pending ? "正在生成草稿…" : "生成一題草稿"}</Button></Actions>
        {pool && !pool.generation_supported && <Message>{pool.generation_unavailable_reason || "請先確認可用母題與年級規則。"}</Message>}
        {pending && <Message role="status">草稿會保存在題族生成紀錄。關閉視窗後仍可回題族管理審閱。</Message>}
      </GeneratePanel>
    </>}
    {error && <Message $error role="alert">{error}<Actions>{!pool && <Button type="button" onClick={() => setRetry((value) => value + 1)}>重新查詢</Button>}{pending && <Button type="button" onClick={() => setBatch((current) => ({ ...current }))}>更新生成進度</Button>}{batch && !pending && batch.candidates?.some((item) => item.question_uuid) && <Button type="button" onClick={() => setDraftRetry((value) => value + 1)}>重新載入草稿</Button>}</Actions></Message>}
  </QuestionFamilyDialog>;
}
const Original = styled.div`padding: 16px; background: var(--app-surface-soft, #f5f6f8); border: 1px solid var(--app-border, #ddd); border-radius: 10px; display: grid; gap: 8px; small { color: var(--app-muted-text, #777); }`;
const SectionHeader = styled.div`display: flex; gap: 16px; align-items: center; justify-content: space-between; margin: 24px 0 14px; h3 { margin: 0; font-size: 16px; } p { margin: 5px 0 0; font-size: 13px; color: var(--app-muted-text, #777); }`;
const Count = styled.span`font-size: 13px; white-space: nowrap; padding: 6px 10px; border-radius: 20px; background: var(--app-surface-soft, #f5f6f8);`;
const Candidates = styled.div`display: grid; gap: 14px;`;
const Candidate = styled.article`border: 1px solid var(--app-border, #ddd); padding: 16px; border-radius: 10px; display: grid; gap: 12px; min-width: 0; details { font-size: 13px; } summary { cursor: pointer; } h4 { margin: 12px 0 6px; }`;
const Meta = styled.div`display: flex; flex-wrap: wrap; gap: 8px; align-items: center; font-size: 12px; color: var(--app-muted-text, #777);`;
const GeneratePanel = styled.section`border: 1px solid var(--app-border, #ddd); border-radius: 12px; padding: 18px; margin-top: 22px; h3 { display: flex; align-items: center; gap: 8px; margin: 0; font-size: 16px; } p { font-size: 13px; color: var(--app-muted-text, #777); margin: 8px 0 14px; } label { display: grid; gap: 5px; font-size: 12px; } select { color: inherit; background: var(--app-input-bg, #fff); padding: 9px; border: 1px solid var(--app-border, #ddd); border-radius: 7px; font: inherit; }`;
const AnalysisPanel = styled.div`margin-top: 22px; min-width: 0;`;
const Actions = styled.div`display: flex; gap: 10px; flex-wrap: wrap; align-items: end;`;
const Button = styled.button`display: inline-flex; align-items: center; gap: 7px; padding: 10px 14px; border-radius: 8px; border: 1px solid var(--app-border, #ddd); background: ${({ $primary }) => $primary ? "var(--app-nav-active-bg, #262e3c)" : "var(--app-surface, #fff)"}; color: ${({ $primary }) => $primary ? "var(--app-nav-active-text, #fff)" : "var(--app-text, #202124)"}; cursor: pointer; font: inherit; font-size: 13px; &:disabled { opacity: .45; cursor: default; }`;
const Empty = styled.div`padding: 20px; border: 1px dashed var(--app-border, #ddd); border-radius: 10px; font-size: 14px; color: var(--app-muted-text, #777);`;
const Message = styled.div`margin-top: 12px; font-size: 13px; color: ${({ $error }) => $error ? "#c64747" : "var(--app-muted-text, #777)"}; overflow-wrap: anywhere;`;
